import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { TitleConfig } from "@/types/TitleConfig";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Loader2, Save } from "lucide-react";
import { ImageUpload } from "@/components/ui/image-upload";
import { uploadBackgroundImage, deleteBackgroundImage } from "@/utils/supabaseStorageUtils";
import { usePageContext } from "@/hooks/usePageContext";

interface SystemSettingsProps {
  titleConfig: TitleConfig;
  setTitleConfig: (config: TitleConfig) => void;
}

const SystemSettings: React.FC<SystemSettingsProps> = ({
  titleConfig,
  setTitleConfig,
}) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const { currentUser } = usePageContext();
  
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleConfig({ ...titleConfig, text: e.target.value });
  };
  
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleConfig({ ...titleConfig, titleColor: e.target.value });
  };
  
  const handleBackgroundImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleConfig({
      ...titleConfig,
      background: {
        type: 'image',
        image: {
          url: e.target.value,
          opacity: titleConfig.background?.image?.opacity || 0.5,
          blur: titleConfig.background?.image?.blur || 0,
          overlayColor: titleConfig.background?.image?.overlayColor || 'rgba(0,0,0,0.5)',
        }
      }
    });
  };

  const handleBackgroundOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const opacity = parseFloat(e.target.value);
    setTitleConfig({
      ...titleConfig,
      background: {
        ...titleConfig.background,
        image: {
          ...(titleConfig.background?.image || { url: '' }),
          opacity: opacity
        }
      }
    });
  };

  const handleBackgroundBlurChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const blur = parseInt(e.target.value);
    setTitleConfig({
      ...titleConfig,
      background: {
        ...titleConfig.background,
        image: {
          ...(titleConfig.background?.image || { url: '' }),
          blur: blur
        }
      }
    });
  };

  const handleBackgroundOverlayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleConfig({
      ...titleConfig,
      background: {
        ...titleConfig.background,
        image: {
          ...(titleConfig.background?.image || { url: '' }),
          overlayColor: e.target.value
        }
      }
    });
  };
  
  const handleLogoToggle = (checked: boolean) => {
    setTitleConfig({
      ...titleConfig,
      logo: {
        ...(titleConfig.logo || { url: '', size: 'medium' }),
        enabled: checked
      }
    });
  };
  
  const handleLogoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleConfig({
      ...titleConfig,
      logo: {
        ...(titleConfig.logo || { enabled: true, size: 'medium' }),
        url: e.target.value
      }
    });
  };
  
  const handleLogoSizeChange = (value: 'small' | 'medium' | 'large') => {
    setTitleConfig({
      ...titleConfig,
      logo: {
        ...(titleConfig.logo || { enabled: true, url: '' }),
        size: value
      }
    });
  };
  
  const handleLogoMaxWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value ? parseInt(e.target.value) : undefined;
    setTitleConfig({
      ...titleConfig,
      logo: {
        ...(titleConfig.logo || { enabled: true, url: '', size: 'medium' }),
        maxWidth: value
      }
    });
  };
  
  const handleBackgroundImageUpload = async (file: File) => {
    if (!currentUser?.id) {
      toast({
        variant: "destructive",
        title: "שגיאה",
        description: "יש להתחבר כדי להעלות תמונות",
      });
      return;
    }

    try {
      const imageUrl = await uploadBackgroundImage(file, currentUser.id);
      if (imageUrl) {
        setTitleConfig({
          ...titleConfig,
          background: {
            type: 'image',
            image: {
              url: imageUrl,
              opacity: titleConfig.background?.image?.opacity || 0.5,
              blur: titleConfig.background?.image?.blur || 0,
              overlayColor: titleConfig.background?.image?.overlayColor || 'rgba(0,0,0,0.5)',
            }
          }
        });
        toast({
          title: "תמונה הועלתה",
          description: "תמונת הרקע הועלתה בהצלחה",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "שגיאה",
        description: "לא ניתן היה להעלות את התמונה",
      });
    }
  };

  const handleBackgroundImageRemove = async () => {
    const currentImageUrl = titleConfig.background?.image?.url;
    if (currentImageUrl) {
      try {
        await deleteBackgroundImage(currentImageUrl);
        setTitleConfig({
          ...titleConfig,
          background: undefined
        });
        toast({
          title: "תמונה הוסרה",
          description: "תמונת הרקע הוסרה בהצלחה",
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "שגיאה",
          description: "לא ניתן היה להסיר את התמונה",
        });
      }
    }
  };
  
  const saveSettings = async () => {
    setIsSaving(true);
    try {
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast({
        title: "הגדרות נשמרו",
        description: "הגדרות המערכת נשמרו בהצלחה",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "שגיאה בשמירה",
        description: "לא ניתן היה לשמור את ההגדרות",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Title Settings */}
      <Card>
        <CardHeader>
          <CardTitle>הגדרות כותרת</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="title">כותרת ראשית</Label>
            <Input
              id="title"
              value={titleConfig.text || ''}
              onChange={handleTitleChange}
              placeholder="הזן כותרת ראשית"
            />
          </div>
          
          <div className="grid gap-2">
            <Label>צבע כותרת</Label>
            <div className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded border" 
                style={{ backgroundColor: titleConfig.titleColor || '#000000' }}
              />
              <Input
                type="color"
                value={titleConfig.titleColor || '#000000'}
                onChange={handleColorChange}
                className="w-20 h-10"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="show-hebrew-date">הצג תאריך עברי</Label>
            <Switch
              id="show-hebrew-date"
              checked={titleConfig.showHebrewDate || false}
              onCheckedChange={(checked) => setTitleConfig({ ...titleConfig, showHebrewDate: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="show-date">הצג תאריך</Label>
            <Switch
              id="show-date"
              checked={titleConfig.showDate || false}
              onCheckedChange={(checked) => setTitleConfig({ ...titleConfig, showDate: checked })}
            />
          </div>

          {titleConfig.showDate && (
            <div className="grid gap-2">
              <Label htmlFor="date-format">פורמט תאריך</Label>
              <Input
                id="date-format"
                value={titleConfig.dateFormat || 'dd/MM/yyyy'}
                onChange={(e) => setTitleConfig({ ...titleConfig, dateFormat: e.target.value })}
                placeholder="למשל: dd/MM/yyyy"
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="show-time">הצג שעה</Label>
            <Switch
              id="show-time"
              checked={titleConfig.showTime || false}
              onCheckedChange={(checked) => setTitleConfig({ ...titleConfig, showTime: checked })}
            />
          </div>

          {titleConfig.showTime && (
            <div className="grid gap-2">
              <Label htmlFor="time-format">פורמט שעה</Label>
              <Input
                id="time-format"
                value={titleConfig.timeFormat || 'HH:mm'}
                onChange={(e) => setTitleConfig({ ...titleConfig, timeFormat: e.target.value })}
                placeholder="למשל: HH:mm"
              />
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Background Settings */}
      <Card>
        <CardHeader>
          <CardTitle>הגדרות רקע</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <Label>העלאת תמונת רקע</Label>
            <ImageUpload
              onImageUpload={handleBackgroundImageUpload}
              onImageRemove={handleBackgroundImageRemove}
              currentImageUrl={titleConfig.background?.image?.url}
              label="העלה תמונת רקע"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="background-image">או הזן כתובת URL לתמונת רקע</Label>
            <Input
              id="background-image"
              value={titleConfig.background?.image?.url || ''}
              onChange={handleBackgroundImageChange}
              placeholder="הזן כתובת URL לתמונת הרקע"
            />
          </div>

          {titleConfig.background?.image?.url && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="background-opacity">שקיפות רקע</Label>
                <Input
                  id="background-opacity"
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={titleConfig.background.image.opacity || 0.5}
                  onChange={handleBackgroundOpacityChange}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="background-blur">טשטוש רקע</Label>
                <Input
                  id="background-blur"
                  type="range"
                  min="0"
                  max="20"
                  step="1"
                  value={titleConfig.background.image.blur || 0}
                  onChange={handleBackgroundBlurChange}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="background-overlay">צבע שכבת כיסוי</Label>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-8 h-8 rounded border" 
                    style={{ backgroundColor: titleConfig.background.image.overlayColor || 'rgba(0,0,0,0.5)' }}
                  />
                  <Input
                    type="color"
                    value={titleConfig.background.image.overlayColor || 'rgba(0,0,0,0.5)'}
                    onChange={handleBackgroundOverlayChange}
                    className="w-20 h-10"
                  />
                </div>
              </div>
            </>
          )}
          
          {titleConfig.background?.image?.url && (
            <div className="mt-4">
              <Label>תצוגה מקדימה</Label>
              <div className="mt-2 border rounded-md overflow-hidden">
                <AspectRatio ratio={16/9}>
                  <div 
                    style={{ 
                      backgroundImage: `url('${titleConfig.background.image.url}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: `blur(${titleConfig.background.image.blur || 0}px)`,
                      opacity: titleConfig.background.image.opacity || 1,
                      position: 'relative',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                  <div 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: titleConfig.background.image.overlayColor || 'rgba(0,0,0,0.5)'
                    }}
                  />
                  <div 
                    className="absolute inset-0 flex items-center justify-center text-white text-2xl font-bold"
                    style={{ color: titleConfig.titleColor || '#ffffff' }}
                  >
                    {titleConfig.text || 'כותרת ראשית'}
                  </div>
                </AspectRatio>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Logo Settings */}
      <Card>
        <CardHeader>
          <CardTitle>הגדרות לוגו</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="logo-enabled">הצג לוגו</Label>
            <Switch
              id="logo-enabled"
              checked={titleConfig.logo?.enabled || false}
              onCheckedChange={handleLogoToggle}
            />
          </div>
          
          {titleConfig.logo?.enabled && (
            <>
              <div className="grid gap-2">
                <Label htmlFor="logo-url">כתובת URL של הלוגו</Label>
                <Input
                  id="logo-url"
                  value={titleConfig.logo.url || ''}
                  onChange={handleLogoUrlChange}
                  placeholder="הזן כתובת URL ללוגו"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="logo-size">גודל לוגו</Label>
                <Select 
                  value={titleConfig.logo.size || 'medium'} 
                  onValueChange={(value: 'small' | 'medium' | 'large') => handleLogoSizeChange(value)}
                >
                  <SelectTrigger id="logo-size">
                    <SelectValue placeholder="בחר גודל" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">קטן</SelectItem>
                    <SelectItem value="medium">בינוני</SelectItem>
                    <SelectItem value="large">גדול</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="logo-max-width">רוחב מקסימלי (פיקסלים)</Label>
                <Input
                  id="logo-max-width"
                  type="number"
                  value={titleConfig.logo.maxWidth || ''}
                  onChange={handleLogoMaxWidthChange}
                  placeholder="הזן רוחב מקסימלי"
                />
              </div>
              
              {titleConfig.logo.url && (
                <div className="mt-4">
                  <Label>תצוגה מקדימה</Label>
                  <div className="mt-2 flex justify-center p-4 bg-slate-100 rounded-md">
                    <img 
                      src={titleConfig.logo.url} 
                      alt="Logo preview" 
                      className="max-h-20"
                      style={{ maxWidth: titleConfig.logo.maxWidth ? `${titleConfig.logo.maxWidth}px` : '100%' }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      <Button 
        onClick={saveSettings} 
        className="w-full"
        disabled={isSaving}
      >
        {isSaving ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
            שומר הגדרות...
          </>
        ) : (
          <>
            <Save className="mr-2 h-4 w-4" /> 
            שמור הגדרות
          </>
        )}
      </Button>
    </div>
  );
};

export default SystemSettings;
